// recova GmbH – Lager: Halle/Vans, Umbuchung, Pickliste, Lieferanten-Bestellungen, Teilestamm, Lagerplätze, Label-Druck.
import { useEffect, useState, useCallback } from "react";
import { API, fmtErr, eur, fileUrl } from "@/lib/api";
import { DashboardLayout, BACKOFFICE_SECTIONS } from "@/components/DashboardLayout";
import { BarcodeScanner } from "@/components/BarcodeScanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Warehouse, Truck, ArrowLeftRight, ClipboardCheck, PackageOpen, Boxes, AlertTriangle, CheckCircle2, MapPin, Tag, Trash2, ScanBarcode } from "lucide-react";
import { toast } from "sonner";

export default function WarehouseDashboard() {
  const [tab, setTab] = useState("halle");
  const [parts, setParts] = useState([]);
  const [stock, setStock] = useState([]);
  const [vans, setVans] = useState([]);
  const [vanFilter, setVanFilter] = useState("VAN-01");
  const [picklist, setPicklist] = useState([]);
  const [partOrders, setPartOrders] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [transfer, setTransfer] = useState({ part_id: "", van_id: "VAN-01", quantity: 1 });
  const [newPart, setNewPart] = useState({ sku: "", name: "", brands: "", min_stock: 1, ek: 0, vk: 0, supplier: "", bin_id: "", initial_quantity: 0 });
  const [bins, setBins] = useState([]);
  const [newBin, setNewBin] = useState({ halle: "Halle 1", regal: "", fach: "" });
  const [scanOpen, setScanOpen] = useState(false);
  const [scanTarget, setScanTarget] = useState("transfer");

  const openScanner = (target) => {
    setScanTarget(target);
    setScanOpen(true);
  };

  const handleScan = (code) => {
    setScanOpen(false);
    const c = code.trim().toLowerCase();
    if (scanTarget === "transfer") {
      const p = parts.find((x) => x.sku.toLowerCase() === c);
      if (!p) return toast.error(`Kein Teil mit Nummer ${code.trim()} gefunden`);
      setTransfer((t) => ({ ...t, part_id: p.id }));
      toast.success(`${p.sku} – ${p.name} ausgewählt`);
    } else {
      const it = picklist.find((x) => x.sku.toLowerCase() === c);
      if (!it) return toast.error(`Keine offene Reservierung für ${code.trim()}`);
      doPick(it.id);
    }
  };

  const loadParts = useCallback(() => API.get("/inventory/parts").then((r) => setParts(r.data)).catch(() => {}), []);
  const loadStock = useCallback((loc, van) => {
    const params = loc === "van" ? { location: "van", van_id: van } : { location: "halle" };
    API.get("/inventory/stock", { params }).then((r) => setStock(r.data)).catch(() => {});
  }, []);
  const loadPicklist = useCallback(() => API.get("/inventory/picklist").then((r) => setPicklist(r.data)).catch(() => {}), []);
  const loadPartOrders = useCallback(() => API.get("/inventory/part-orders").then((r) => setPartOrders(r.data)).catch(() => {}), []);
  const loadLow = useCallback(() => API.get("/inventory/low-stock").then((r) => setLowStock(r.data)).catch(() => {}), []);

  useEffect(() => {
    loadParts();
    API.get("/inventory/vans").then((r) => setVans(r.data)).catch(() => {});
    API.get("/inventory/bins").then((r) => setBins(r.data)).catch(() => {});
    loadLow();
  }, [loadParts, loadLow]);
  useEffect(() => {
    if (tab === "halle") loadStock("halle");
    if (tab === "vans") loadStock("van", vanFilter);
    if (tab === "pickliste") loadPicklist();
    if (tab === "bestellungen") loadPartOrders();
  }, [tab, vanFilter, loadStock, loadPicklist, loadPartOrders]);

  const adjustQty = async (item, qty) => {
    try {
      await API.post("/inventory/stock/adjust", { part_id: item.part_id, location: item.location, bin: item.bin, van_id: item.van_id, quantity: Number(qty) });
      toast.success("Bestand aktualisiert");
      loadStock(item.location, vanFilter);
      loadLow();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const doTransfer = async () => {
    if (!transfer.part_id || transfer.quantity < 1) return toast.error("Teil und Menge wählen");
    try {
      await API.post("/inventory/transfer", { ...transfer, quantity: Number(transfer.quantity) });
      toast.success("Umbuchung Halle → Fahrzeug erfolgt");
      loadLow();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const doPick = async (id) => {
    try {
      await API.post(`/inventory/picklist/${id}/pick`);
      toast.success("Teil entnommen & ins Fahrzeug gebucht");
      loadPicklist();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const deletePicklistItem = async (p) => {
    if (!window.confirm(`Reservierung ${p.sku} – ${p.part_name} wirklich aus der Pickliste löschen?`)) return;
    try {
      await API.delete(`/inventory/picklist/${p.id}`);
      toast.success("Reservierung gelöscht");
      setPicklist(picklist.filter((x) => x.id !== p.id));
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const doDelivered = async (id) => {
    try {
      await API.post(`/inventory/part-orders/${id}/delivered`);
      toast.success("Wareneingang gebucht – Auftrag bereit für Zweittermin");
      loadPartOrders();
      loadLow();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const deletePartOrder = async (po) => {
    if (!window.confirm(`Bestellung ${po.part_name} (${po.quantity}x) für Auftrag ${po.order_number} wirklich löschen?`)) return;
    try {
      await API.delete(`/inventory/part-orders/${po.id}`);
      toast.success("Bestellung gelöscht");
      setPartOrders(partOrders.filter((x) => x.id !== po.id));
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const createPart = async () => {
    if (!newPart.name) return toast.error("Bezeichnung erforderlich (SKU wird automatisch vergeben, wenn leer)");
    try {
      const r = await API.post("/inventory/parts", {
        ...newPart,
        sku: newPart.sku || null,
        bin_id: newPart.bin_id || null,
        brands: newPart.brands.split(",").map((s) => s.trim()).filter(Boolean),
        min_stock: Number(newPart.min_stock), ek: Number(newPart.ek), vk: Number(newPart.vk),
        initial_quantity: Number(newPart.initial_quantity),
      });
      toast.success(`Teil angelegt – Nummer ${r.data.sku}`);
      setNewPart({ sku: "", name: "", brands: "", min_stock: 1, ek: 0, vk: 0, supplier: "", bin_id: "", initial_quantity: 0 });
      loadParts();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const createBin = async () => {
    if (!newBin.regal || !newBin.fach) return toast.error("Regal und Fach erforderlich");
    try {
      const r = await API.post("/inventory/bins", newBin);
      toast.success(`Lagerplatz angelegt: ${r.data.code}`);
      setNewBin({ halle: "Halle 1", regal: "", fach: "" });
      const b = await API.get("/inventory/bins");
      setBins(b.data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const deleteBin = async (id) => {
    try {
      await API.delete(`/inventory/bins/${id}`);
      toast.success("Lagerplatz gelöscht");
      setBins(bins.filter((b) => b.id !== id));
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const deletePart = async (p) => {
    if (!window.confirm(`Teil ${p.sku} – ${p.name} wirklich endgültig löschen?`)) return;
    try {
      await API.delete(`/inventory/parts/${p.id}`);
      toast.success("Teil gelöscht");
      setParts(parts.filter((x) => x.id !== p.id));
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const StockTable = ({ items, showBin, testid }) => (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-400">
            <th className="pb-2 pr-4">SKU</th><th className="pb-2 pr-4">Bezeichnung</th>{showBin && <th className="pb-2 pr-4">Lagerplatz</th>}<th className="pb-2 pr-4">Bestand</th><th className="pb-2 pr-4">VK</th><th className="pb-2">Anpassen</th>
          </tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.id} className="border-b border-slate-100" data-testid={`stock-row-${s.sku}`}>
              <td className="py-2.5 pr-4 font-mono2 font-semibold">{s.sku}</td>
              <td className="py-2.5 pr-4">{s.name}</td>
              {showBin && <td className="py-2.5 pr-4 text-slate-500">{s.bin}</td>}
              <td className="py-2.5 pr-4">
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${s.quantity < (s.min_stock || 0) ? "bg-red-100 text-red-700" : "bg-emerald-100 text-[#1b4332]"}`}>{s.quantity}</span>
              </td>
              <td className="py-2.5 pr-4">{eur(s.vk)}</td>
              <td className="py-2.5">
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => adjustQty(s, s.quantity + 1)} data-testid={`stock-inc-${s.id}`}>+1</Button>
                  <Button size="sm" variant="outline" onClick={() => s.quantity > 0 && adjustQty(s, s.quantity - 1)} data-testid={`stock-dec-${s.id}`}>−1</Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length === 0 && <p className="py-8 text-center text-slate-400">Kein Bestand vorhanden.</p>}
    </div>
  );

  return (
    <DashboardLayout title="Lagerverwaltung (Halle & Fahrzeuge)" subtitle="Backoffice – Lager"
      sections={BACKOFFICE_SECTIONS.map((s) => ({ ...s, active: s.to === "/app/lager" }))}
      active={tab} onNav={setTab}
      nav={[
        { key: "halle", label: "Regallager Halle", icon: <Warehouse className="h-4 w-4" /> },
        { key: "vans", label: "Fahrzeuglager", icon: <Truck className="h-4 w-4" /> },
        { key: "umbuchen", label: "Umbuchen", icon: <ArrowLeftRight className="h-4 w-4" /> },
        { key: "pickliste", label: "Pickliste (Morgen)", icon: <ClipboardCheck className="h-4 w-4" />, badge: picklist.length },
        { key: "bestellungen", label: "Lieferanten-Bestellungen", icon: <PackageOpen className="h-4 w-4" /> },
        { key: "teile", label: "Teilestamm", icon: <Boxes className="h-4 w-4" /> },
        { key: "plaetze", label: "Lagerplätze", icon: <MapPin className="h-4 w-4" /> },
      ]}>

      {lowStock.length > 0 && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-orange-200 bg-orange-50 p-4" data-testid="low-stock-banner">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-orange-500" />
          <div className="text-sm">
            <p className="font-semibold text-orange-800">Mindestbestand unterschritten ({lowStock.length}):</p>
            <p className="text-orange-700">{lowStock.map((p) => `${p.sku} ${p.name} (${p.halle_total}/${p.min_stock})`).join(" · ")}</p>
          </div>
        </div>
      )}

      {tab === "halle" && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="font-heading mb-4 text-lg font-semibold text-[#1b4332]">Zentrales Regallager – Halle 1</h3>
          <StockTable items={stock} showBin testid="halle-stock-table" />
        </div>
      )}

      {tab === "vans" && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-3">
            <Label>Fahrzeug:</Label>
            <select className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm" value={vanFilter} onChange={(e) => setVanFilter(e.target.value)} data-testid="van-select">
              {vans.map((v) => <option key={v.van_id} value={v.van_id}>{v.van_id} – {v.technician} ({v.van_plate})</option>)}
            </select>
          </div>
          <StockTable items={stock} showBin={false} testid="van-stock-table" />
        </div>
      )}

      {tab === "umbuchen" && (
        <div className="max-w-lg rounded-2xl border border-slate-200 bg-white p-6" data-testid="transfer-form">
          <h3 className="font-heading text-lg font-semibold text-[#1b4332]">Umbuchung: Halle → Fahrzeug</h3>
          <div className="mt-4 space-y-3">
            <div>
              <div className="flex items-center justify-between">
                <Label>Ersatzteil</Label>
                <button type="button" className="inline-flex items-center gap-1 rounded-md border border-[#028090] px-2 py-1 text-xs font-semibold text-[#028090] transition-colors hover:bg-cyan-50"
                  onClick={() => openScanner("transfer")} data-testid="transfer-scan-button">
                  <ScanBarcode className="h-3.5 w-3.5" /> Scannen
                </button>
              </div>
              <select className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm" value={transfer.part_id} onChange={(e) => setTransfer({ ...transfer, part_id: e.target.value })} data-testid="transfer-part-select">
                <option value="">Bitte wählen…</option>
                {parts.map((p) => <option key={p.id} value={p.id}>{p.sku} – {p.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Ziel-Fahrzeug</Label>
              <select className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm" value={transfer.van_id} onChange={(e) => setTransfer({ ...transfer, van_id: e.target.value })} data-testid="transfer-van-select">
                {vans.map((v) => <option key={v.van_id} value={v.van_id}>{v.van_id} – {v.technician}</option>)}
              </select>
            </div>
            <div>
              <Label>Menge</Label>
              <Input type="number" min="1" className="mt-1" value={transfer.quantity} onChange={(e) => setTransfer({ ...transfer, quantity: e.target.value })} data-testid="transfer-quantity" />
            </div>
            <Button className="bg-[#1b4332] hover:bg-[#143326]" onClick={doTransfer} data-testid="transfer-submit"><ArrowLeftRight className="mr-2 h-4 w-4" /> Umbuchen</Button>
          </div>
        </div>
      )}

      {tab === "pickliste" && (
        <div className="space-y-3" data-testid="picklist-panel">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-slate-500">Reservierte Teile, die Techniker vor Abfahrt in der Halle entnehmen müssen.</p>
            <Button size="sm" variant="outline" onClick={() => openScanner("picklist")} data-testid="picklist-scan-button">
              <ScanBarcode className="mr-1.5 h-4 w-4" /> Scannen & entnehmen
            </Button>
          </div>
          {picklist.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`picklist-item-${p.id}`}>
              <div>
                <p className="font-semibold text-[#0f172a]">{p.sku} – {p.part_name} <span className="text-slate-400">({p.quantity}x)</span></p>
                <p className="text-sm text-slate-500">Lagerplatz: <span className="font-mono2 font-semibold text-[#028090]">{p.bin}</span> · Fahrzeug {p.van_id} · Auftrag {p.order_number} {p.customer && `· ${p.customer.zip} ${p.customer.city}`}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" className="bg-[#1b4332] hover:bg-[#143326]" onClick={() => doPick(p.id)} data-testid={`pick-button-${p.id}`}>
                  <CheckCircle2 className="mr-1.5 h-4 w-4" /> Entnommen & einladen
                </Button>
                <Button size="sm" variant="ghost" onClick={() => deletePicklistItem(p)} data-testid={`picklist-delete-${p.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-red-400" />
                </Button>
              </div>
            </div>
          ))}
          {picklist.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white py-10 text-center text-slate-400" data-testid="picklist-empty">Keine offenen Reservierungen – alles eingeladen.</p>}
        </div>
      )}

      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onScan={handleScan}
        title={scanTarget === "picklist" ? "Reservierung scannen – wird entnommen" : "Teilenummer scannen"} />

      {tab === "bestellungen" && (
        <div className="space-y-3" data-testid="part-orders-panel">
          {partOrders.map((po) => (
            <div key={po.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`part-order-${po.id}`}>
              <div>
                <p className="font-semibold text-[#0f172a]">{po.part_name} <span className="text-slate-400">({po.quantity}x)</span></p>
                <p className="text-sm text-slate-500">Auftrag {po.order_number} · bestellt {new Date(po.created_at).toLocaleDateString("de-DE")}</p>
              </div>
              <div className="flex items-center gap-2">
                {po.status === "bestellt" ? (
                  <Button size="sm" className="bg-[#028090] hover:bg-[#026673]" onClick={() => doDelivered(po.id)} data-testid={`delivered-button-${po.id}`}>Wareneingang buchen</Button>
                ) : (
                  <span className="badge-eco">Geliefert {po.delivered_at && new Date(po.delivered_at).toLocaleDateString("de-DE")}</span>
                )}
                <Button size="sm" variant="ghost" onClick={() => deletePartOrder(po)} data-testid={`part-order-delete-${po.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-red-400" />
                </Button>
              </div>
            </div>
          ))}
          {partOrders.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white py-10 text-center text-slate-400">Keine Lieferanten-Bestellungen.</p>}
        </div>
      )}

      {tab === "plaetze" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2" data-testid="bins-panel">
            <h3 className="font-heading mb-4 text-lg font-semibold text-[#1b4332]">Lagerplätze ({bins.length})</h3>
            <div className="grid max-h-96 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
              {bins.map((b) => (
                <div key={b.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`bin-${b.id}`}>
                  <span className="font-mono2 text-xs font-semibold">{b.code}</span>
                  <Button size="sm" variant="ghost" onClick={() => deleteBin(b.id)} data-testid={`bin-delete-${b.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-400" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="new-bin-form">
            <h3 className="font-heading text-lg font-semibold text-[#1b4332]">Neuer Lagerplatz</h3>
            <div className="mt-4 space-y-3">
              <div>
                <Label className="text-xs">Halle</Label>
                <Input className="mt-1" value={newBin.halle} onChange={(e) => setNewBin({ ...newBin, halle: e.target.value })} data-testid="new-bin-halle" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Regal</Label>
                  <Input className="mt-1" placeholder="z. B. E" value={newBin.regal} onChange={(e) => setNewBin({ ...newBin, regal: e.target.value })} data-testid="new-bin-regal" />
                </div>
                <div>
                  <Label className="text-xs">Fach</Label>
                  <Input className="mt-1" placeholder="z. B. 03" value={newBin.fach} onChange={(e) => setNewBin({ ...newBin, fach: e.target.value })} data-testid="new-bin-fach" />
                </div>
              </div>
              <Button className="w-full bg-[#1b4332] hover:bg-[#143326]" onClick={createBin} data-testid="new-bin-submit">Lagerplatz anlegen</Button>
              <p className="text-xs text-slate-400">Belegte Lagerplätze (mit Bestand) können nicht gelöscht werden.</p>
            </div>
          </div>
        </div>
      )}

      {tab === "teile" && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2" data-testid="parts-table-panel">
            <h3 className="font-heading mb-4 text-lg font-semibold text-[#1b4332]">Teilestamm</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-400">
                    <th className="pb-2 pr-4">SKU</th><th className="pb-2 pr-4">Name</th><th className="pb-2 pr-4">Marken</th><th className="pb-2 pr-4">Min.</th><th className="pb-2 pr-4">EK</th><th className="pb-2 pr-4">VK</th><th className="pb-2 pr-4">Label</th><th className="pb-2">Löschen</th>
                  </tr>
                </thead>
                <tbody>
                  {parts.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100" data-testid={`part-row-${p.sku}`}>
                      <td className="py-2 pr-4 font-mono2 font-semibold">{p.sku}</td>
                      <td className="py-2 pr-4">{p.name}</td>
                      <td className="py-2 pr-4 text-slate-500">{(p.brands || []).join(", ")}</td>
                      <td className="py-2 pr-4">{p.min_stock}</td>
                      <td className="py-2 pr-4">{eur(p.ek)}</td>
                      <td className="py-2 pr-4">{eur(p.vk)}</td>
                      <td className="py-2 pr-4">
                        <a href={fileUrl(`/api/inventory/parts/${p.id}/label`)} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-[#1b4332] transition-colors hover:bg-[#e8f0ec]"
                          data-testid={`label-print-${p.sku}`}>
                          <Tag className="h-3.5 w-3.5" /> Label
                        </a>
                      </td>
                      <td className="py-2">
                        <Button size="sm" variant="ghost" onClick={() => deletePart(p)} data-testid={`part-delete-${p.sku}`}>
                          <Trash2 className="h-3.5 w-3.5 text-red-400" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="new-part-form">
            <h3 className="font-heading text-lg font-semibold text-[#1b4332]">Neues Teil anlegen</h3>
            <div className="mt-4 space-y-3">
              <Input placeholder="Nummer (leer = automatisch, z. B. ET-011)" value={newPart.sku} onChange={(e) => setNewPart({ ...newPart, sku: e.target.value })} data-testid="new-part-sku" />
              <Input placeholder="Bezeichnung *" value={newPart.name} onChange={(e) => setNewPart({ ...newPart, name: e.target.value })} data-testid="new-part-name" />
              <Input placeholder="Marken (Komma-getrennt)" value={newPart.brands} onChange={(e) => setNewPart({ ...newPart, brands: e.target.value })} data-testid="new-part-brands" />
              <div className="grid grid-cols-3 gap-2">
                <Input type="number" placeholder="Min." value={newPart.min_stock} onChange={(e) => setNewPart({ ...newPart, min_stock: e.target.value })} data-testid="new-part-min" />
                <Input type="number" placeholder="EK €" value={newPart.ek} onChange={(e) => setNewPart({ ...newPart, ek: e.target.value })} data-testid="new-part-ek" />
                <Input type="number" placeholder="VK €" value={newPart.vk} onChange={(e) => setNewPart({ ...newPart, vk: e.target.value })} data-testid="new-part-vk" />
              </div>
              <div>
                <Label className="text-xs">Lagerplatz (Halle)</Label>
                <select className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm" value={newPart.bin_id} onChange={(e) => setNewPart({ ...newPart, bin_id: e.target.value })} data-testid="new-part-bin">
                  <option value="">Kein Lagerplatz</option>
                  {bins.map((b) => <option key={b.id} value={b.id}>{b.code}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Anfangsbestand</Label>
                <Input type="number" min="0" className="mt-1" value={newPart.initial_quantity} onChange={(e) => setNewPart({ ...newPart, initial_quantity: e.target.value })} data-testid="new-part-initial-qty" />
              </div>
              <Input placeholder="Lieferant" value={newPart.supplier} onChange={(e) => setNewPart({ ...newPart, supplier: e.target.value })} data-testid="new-part-supplier" />
              <Button className="w-full bg-[#1b4332] hover:bg-[#143326]" onClick={createPart} data-testid="new-part-submit">Teil anlegen</Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
